import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import Setbar from './Setbar'
import PresentTiles from './PresentTiles'
import PresentControls from './PresentControls'

function PresentView({ title, date, songs, previewLang, onAddSong, hasChanges, onSave }) {
  const [activeTileIndex, setActiveTileIndex] = useState(null)
  const [outputLang, setOutputLang] = useState({ en: true, ml: true })
  const [isLive, setIsLive] = useState(false)
  const [overlay, setOverlay] = useState(null) // null | 'clear' | 'black'
  const tilesRef = useRef(null)

  // Build flat tile list: { songIndex, verseIndex, song, verse, verse2, label }
  const tiles = useMemo(() => {
    const result = []
    songs.forEach((song, songIndex) => {
      const numLabels = song.lyrics && song.lyrics.some(v => v[0] && /^\d/.test(v[0][0]))
      const verses = song.lyrics2 || []
      verses.forEach((verse2, verseIndex) => {
        const verse = (song.lyrics && song.lyrics[verseIndex]) || []
        let label
        if (numLabels) {
          if (verse2[0] && /^\d/.test(verse2[0][0])) label = `Verse ${verse2[0][0]}`
          else if (!verse[0] || !verse[0].length) label = 'Verse 99'
          else label = 'Chorus'
        } else {
          label = `Verse ${verseIndex + 1}`
        }
        result.push({ songIndex, verseIndex, song, verse, verse2, label })
      })
    })
    return result
  }, [songs])

  const activeTile = activeTileIndex !== null ? tiles[activeTileIndex] : null
  const activeSongIndex = activeTile ? activeTile.songIndex : null

  const handleTileSelect = useCallback((index) => {
    setActiveTileIndex(index)
    setOverlay(null)
  }, [])

  const handleOverlayToggle = useCallback((type) => {
    setOverlay(prev => prev === type ? null : type)
  }, [])

  const handleSongClick = useCallback((songIndex) => {
    const tileIndex = tiles.findIndex(t => t.songIndex === songIndex)
    if (tileIndex !== -1) {
      setActiveTileIndex(tileIndex)
      setOverlay(null)
    }
  }, [tiles])

  const scrollToSong = useCallback((songIndex) => {
    if (tilesRef.current) {
      const divider = tilesRef.current.querySelector(`[data-song-index="${songIndex}"]`)
      if (divider) divider.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  const handleSongClickFull = useCallback((songIndex) => {
    handleSongClick(songIndex)
    scrollToSong(songIndex)
  }, [handleSongClick, scrollToSong])

  // Find first tile index for each song
  const songFirstTileIndex = useMemo(() => {
    const result = []
    let lastSong = -1
    tiles.forEach((tile, index) => {
      if (tile.songIndex !== lastSong) {
        result.push(index)
        lastSong = tile.songIndex
      }
    })
    return result
  }, [tiles])

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.target.tagName === 'INPUT') return

      const total = tiles.length
      if (total === 0) return

      let newIndex = activeTileIndex

      if (e.key === 'ArrowRight' && !e.ctrlKey) {
        e.preventDefault()
        newIndex = activeTileIndex === null ? 0 : Math.min(activeTileIndex + 1, total - 1)
      } else if (e.key === 'ArrowLeft' && !e.ctrlKey) {
        e.preventDefault()
        newIndex = activeTileIndex === null ? 0 : Math.max(activeTileIndex - 1, 0)
      } else if (e.key === 'ArrowRight' && e.ctrlKey) {
        e.preventDefault()
        if (activeTileIndex !== null) {
          const currentSong = tiles[activeTileIndex].songIndex
          const nextSongStart = songFirstTileIndex.find(i => tiles[i].songIndex > currentSong)
          if (nextSongStart !== undefined) newIndex = nextSongStart
        } else {
          newIndex = 0
        }
      } else if (e.key === 'ArrowLeft' && e.ctrlKey) {
        e.preventDefault()
        if (activeTileIndex !== null) {
          const currentSong = tiles[activeTileIndex].songIndex
          const prevStarts = songFirstTileIndex.filter(i => tiles[i].songIndex < currentSong)
          if (prevStarts.length > 0) newIndex = prevStarts[prevStarts.length - 1]
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (activeTileIndex !== null) {
          const currentSong = tiles[activeTileIndex].songIndex
          const nextSongStart = songFirstTileIndex.find(i => tiles[i].songIndex > currentSong)
          if (nextSongStart !== undefined) newIndex = nextSongStart
        } else {
          newIndex = 0
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (activeTileIndex !== null) {
          const currentSong = tiles[activeTileIndex].songIndex
          const prevStarts = songFirstTileIndex.filter(i => tiles[i].songIndex < currentSong)
          if (prevStarts.length > 0) newIndex = prevStarts[prevStarts.length - 1]
        }
      } else {
        return
      }

      if (newIndex !== activeTileIndex) {
        handleTileSelect(newIndex)
        const container = tilesRef.current
        if (container) {
          const tileEls = container.querySelectorAll('.present-tile')
          if (tileEls[newIndex]) {
            tileEls[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeTileIndex, tiles, songFirstTileIndex, handleTileSelect])

  return (
    <div className="present-view">
      <Setbar
        songs={songs}
        activeSongIndex={activeSongIndex}
        previewLang={previewLang}
        hasChanges={hasChanges}
        onSongClick={handleSongClickFull}
        onAddSong={onAddSong}
        onSave={onSave}
      />
      <div className="present-cols">
        <div className="present-col present-col--middle">
          <div className="present-col-label">Control</div>
          <PresentTiles
            ref={tilesRef}
            tiles={tiles}
            activeTileIndex={activeTileIndex}
            activeSongIndex={activeSongIndex}
            previewLang={previewLang}
            onTileSelect={handleTileSelect}
            songs={songs}
          />
        </div>
        <div className="present-col">
          <div className="present-col-label">Preview</div>
          <PresentControls
            activeTile={activeTile}
            outputLang={outputLang}
            onOutputLangToggle={(lang) => setOutputLang(prev => ({ ...prev, [lang]: !prev[lang] }))}
            overlay={overlay}
            onOverlayToggle={handleOverlayToggle}
            isLive={isLive}
            onLiveToggle={() => setIsLive(prev => !prev)}
          />
        </div>
      </div>
    </div>
  )
}

export default PresentView
